import { useAuth0 } from '@auth0/auth0-react';
import {
  ConfirmUserCompanyResponse,
  GetOnboardingStatusResponse,
} from '@dreamai/types';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { PusherProvider } from './contexts/PusherContext';
import { Providers as DependencyProviders } from './providers';
import './utils/testDependencyUpdate';

import api from './api';
import AuthErrorPage from './components/AuthErrorPage';
import AccountDeactivatedPage, {
  AccountDeactivatedInfo,
  parseAccountDeactivated,
} from './components/AccountDeactivatedPage';
import LoginPage from './components/LoginPage';
import MaintenancePage from './components/MaintenancePage';
import AppLoading from './components/misc/AppLoading';

import { CommentsProvider } from './contexts/CommentsContext';
import { FormCaptureProvider } from './contexts/FormCaptureContext';
import { LinkedThreadsProvider } from './contexts/LinkedThreadsContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { NewThreadProvider } from './contexts/NewThreadContext';
import { ThreadLoadingProvider } from './contexts/ThreadLoadingContext';

import BookingPage from './components/BookingPage';
import ConversationalUI from './components/ConversationalUI';
import SharedProjectAppPage from './components/SharedProjectAppPage';
import SharedArtifactPage from './components/SharedArtifactPage';
import SharedViewPage from './components/SharedViewPage';
import WebRTCVoiceChat from './components/WebRTCVoiceChat';
import SharedTodayUpdatePage from './components/chat/SharedTodayUpdatePage';
import CreditsPusherListener from './components/common/CreditsPusherListener';
import TFAPusherListener from './components/common/TFAPusherListener';
import GoogleDrivePickerListener from './components/common/GoogleDrivePickerListener';
import GrantDriveAccessPage from './components/GrantDriveAccessPage';
import CircleInvitePage from './components/CircleInvitePage';
import OnboardingConnectPage from './components/OnboardingConnectPage';
import OnboardingWizardPage from './components/onboardingWeb/OnboardingWizardPage';
import WaSuggestionsPage from './components/WaSuggestionsPage';
import WaBriefingPage from './components/WaBriefingPage';
import WaTourPage from './components/WaTourPage';
import JoinPage from './components/JoinPage';
import ReferralRedirectPage from './components/ReferralRedirectPage';
import EmailDraftLinkPage from './components/EmailDraftLinkPage';
import TodoListGuestPage from './components/todo-list-guest/TodoListGuestPage';
import OnboardingFlow from './components/onboarding/OnboardingFlow';
import { CompanyProvider, useCompany } from './contexts/CompanyContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import './index.scss';

const DailyBriefingDemo = React.lazy(
  () => import('./demo/dailyBriefing/DailyBriefingDemo'),
);
const WhatsNewPage = React.lazy(
  () => import('./demo/dailyBriefing/WhatsNewPage'),
);

const AppContent: React.FC = () => {
  const {
    isAuthenticated,
    isLoading,
    error: authError,
    getAccessTokenSilently,
  } = useAuth0();
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [bootstrapRefusal, setBootstrapRefusal] = useState<string | null>(null);
  const [accountDeactivated, setAccountDeactivated] =
    useState<AccountDeactivatedInfo | null>(null);
  const {
    setMemoryEnabled,
    setHasSubscription,
    setCredits,
    setResetsAt,
    setHasPersonalAssistant,
    isMaintenanceExempt,
    loading: companyLoading,
  } = useCompany();

  const maintenanceModeActive =
    process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  const navigate = useNavigate();

  useEffect(() => {
    const checkCompanyStatus = async (): Promise<void> => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          const response = await api.get<ConfirmUserCompanyResponse>(
            '/confirm-user-company',
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          setMemoryEnabled(response.data.memory_enabled || false);
          setHasSubscription(response.data.has_subscription || false);
          setCredits(response.data.credits ?? 0);
          setResetsAt(response.data.resets_at ?? null);
          setHasCompany(response.data.has_company);
          setHasPersonalAssistant(
            response.data.has_personal_assistant || false,
          );

          // Check onboarding status — redirect if incomplete
          try {
            const onboardingRes = await api.get<GetOnboardingStatusResponse>(
              '/onboarding/status',
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (
              onboardingRes.data.success &&
              !onboardingRes.data.status.onboardingCompleted
            ) {
              // Only redirect if not already on /onboarding (preserves query
              // params from external redirects like Airwallex checkout)
              if (
                !window.location.pathname.startsWith('/onboarding') &&
                window.location.pathname !== '/whats-new'
              ) {
                navigate('/onboarding', { replace: true });
              }
            }
          } catch (onboardingErr) {
            // If status endpoint fails (e.g. no row yet), skip redirect
            console.warn('Could not check onboarding status:', onboardingErr);
          }
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 403) {
            const body: unknown = error.response.data;
            const deactivated = parseAccountDeactivated(body);
            if (deactivated) {
              setAccountDeactivated(deactivated);
              return;
            }
            const message =
              typeof body === 'object' &&
              body !== null &&
              'error' in body &&
              typeof body.error === 'string'
                ? body.error
                : 'This login is not linked to a Dream account.';
            setBootstrapRefusal(message);
            return;
          }
          console.error('Error checking company status:', error);
        }
      }
    };

    checkCompanyStatus();
  }, [isAuthenticated, navigate, getAccessTokenSilently]);

  // An admin can deactivate a member mid-session; from then on every request
  // is refused with `account_deactivated`, so swap the app for the notice.
  useEffect(() => {
    const id = api.interceptors.response.use(undefined, (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        const deactivated = parseAccountDeactivated(error.response.data);
        if (deactivated) setAccountDeactivated(deactivated);
      }
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  // Circle invite link: a personal link works without signing in; the page
  // itself asks for sign-in when the link was shared with a group.
  if (window.location.pathname.startsWith('/circle/')) {
    return (
      <Routes>
        <Route path='/circle/:code' element={<CircleInvitePage />} />
      </Routes>
    );
  }

  // "Open draft" links from WhatsApp: no auth involved, and the page must
  // hand off to Gmail/Outlook without first showing the app loader.
  if (window.location.pathname.startsWith('/d/')) {
    return (
      <Routes>
        <Route path='/d/:token' element={<EmailDraftLinkPage />} />
      </Routes>
    );
  }

  if (isLoading) {
    return <AppLoading />;
  }

  // Auth0 redirected back with ?error=… (auth0-react leaves the params in
  // place). The login page must not be shown here: the user just came back
  // from Auth0 with a denial and needs to see why, not the form again.
  if (
    authError &&
    !isAuthenticated &&
    new URLSearchParams(window.location.search).has('error')
  ) {
    return <AuthErrorPage />;
  }

  // If the page is a shared update, for now just load only that route, no difference between
  // authenticated or not authenticated app
  if (window.location.pathname.startsWith('/updates/shared')) {
    return (
      <Routes>
        <Route
          path='/updates/shared/:updateId/:imageId'
          element={<SharedTodayUpdatePage />}
        />
      </Routes>
    );
  }

  // Public routes — render regardless of auth state so logged-in users can
  // view shared pages / booking pages without being swallowed by the app shell.
  // This check is intentionally ABOVE the hasCompany gate so authenticated users
  // don't have to wait for checkCompanyStatus before seeing shared content.
  if (
    !isLoading &&
    (window.location.pathname.startsWith('/booking/') ||
      window.location.pathname.startsWith('/shared/') ||
      window.location.pathname.startsWith('/artifact/') ||
      window.location.pathname.startsWith('/shared-view/') ||
      window.location.pathname.startsWith('/onboarding-connect') ||
      window.location.pathname === '/onboarding' ||
      window.location.pathname === '/onboarding-prototype' ||
      window.location.pathname.startsWith('/wa-suggestions') ||
      window.location.pathname.startsWith('/wa-briefing') ||
      window.location.pathname.startsWith('/wa-tour') ||
      window.location.pathname === '/whats-new' ||
      window.location.pathname.startsWith('/demo/daily-briefing') ||
      window.location.pathname === '/join' ||
      window.location.pathname.startsWith('/ref/') ||
      window.location.pathname.startsWith('/to-do-list/guest/'))
  ) {
    return (
      <Routes>
        <Route path='/booking/:key' element={<BookingPage />} />
        <Route path='/shared/:shareToken' element={<SharedProjectAppPage />} />
        <Route path='/artifact/:shareToken' element={<SharedArtifactPage />} />
        <Route path='/shared-view/:shareToken' element={<SharedViewPage />} />
        <Route path='/onboarding-connect' element={<OnboardingConnectPage />} />
        <Route path='/onboarding' element={<OnboardingWizardPage />} />
        <Route path='/wa-suggestions' element={<WaSuggestionsPage />} />
        <Route path='/wa-briefing' element={<WaBriefingPage />} />
        <Route path='/wa-tour' element={<WaTourPage />} />
        <Route
          path='/whats-new'
          element={
            <React.Suspense fallback={null}>
              <WhatsNewPage />
            </React.Suspense>
          }
        />
        <Route
          path='/demo/daily-briefing/*'
          element={
            <React.Suspense fallback={null}>
              <DailyBriefingDemo />
            </React.Suspense>
          }
        />
        <Route path='/join' element={<JoinPage />} />
        <Route path='/ref/:code' element={<ReferralRedirectPage />} />
        <Route
          path='/to-do-list/guest/:token'
          element={<TodoListGuestPage />}
        />
        <Route path='*' element={<LoginPage />} />
        <Route path='/voice-chat' element={<WebRTCVoiceChat />} />
      </Routes>
    );
  }

  if (isAuthenticated && accountDeactivated) {
    return <AccountDeactivatedPage info={accountDeactivated} />;
  }

  if (isAuthenticated && bootstrapRefusal) {
    return <AuthErrorPage description={bootstrapRefusal} />;
  }

  if (isAuthenticated && hasCompany === null) {
    return <AppLoading />;
  }

  if (!isLoading && !isAuthenticated) {
    return <LoginPage />;
  }

  if (isAuthenticated) {
    if (maintenanceModeActive && !isMaintenanceExempt) {
      // Wait for /user-details so exempt users aren't flashed the placeholder
      if (companyLoading) {
        return <AppLoading />;
      }
      return <MaintenancePage />;
    }

    // Drive access grant link (sent over WhatsApp etc.) — the picker needs the
    // user's session, so it lands here rather than on a public page.
    if (window.location.pathname.startsWith('/grant-access/')) {
      return (
        <Routes>
          <Route
            path='/grant-access/:requestId'
            element={<GrantDriveAccessPage />}
          />
        </Routes>
      );
    }

    // Onboarding flow — rendered outside normal layout
    if (
      window.location.pathname === '/onboarding' ||
      window.location.pathname.startsWith('/onboarding/')
    ) {
      return (
        <PusherProvider>
          <Routes>
            <Route path='/onboarding' element={<OnboardingFlow />} />
          </Routes>
        </PusherProvider>
      );
    }

    return (
      <PusherProvider>
        <CreditsPusherListener />
        <TFAPusherListener />
        <GoogleDrivePickerListener />
        <DependencyProviders>
          <MessagingProvider>
            <NewThreadProvider>
              <ThreadLoadingProvider>
                <FormCaptureProvider>
                  <CommentsProvider>
                    <LinkedThreadsProvider>
                      <ConversationalUI />
                    </LinkedThreadsProvider>
                  </CommentsProvider>
                </FormCaptureProvider>
              </ThreadLoadingProvider>
            </NewThreadProvider>
          </MessagingProvider>
        </DependencyProviders>
      </PusherProvider>
    );
  }

  /**
   * Fallback to loading forever, this should never logically happen, if it does, there is a bug upwards
   */
  return <AppLoading />;
};

const App: React.FC = () => {
  return (
    <CompanyProvider>
      <FeatureFlagsProvider>
        <AppContent />
      </FeatureFlagsProvider>
    </CompanyProvider>
  );
};

export default App;
